/**
 * Decléor — dismissible site-wide banner (sections/site-banner.liquid).
 *
 * Three jobs:
 *   1. Decide whether the visitor has already closed this banner, and reveal it only if not. The
 *      markup ships hidden, so a dismissed banner never flashes before the script runs.
 *   2. Persist the dismissal in localStorage, under a key that includes the section id AND the
 *      merchant's "Version" setting — bumping Version is how a changed message reaches people who
 *      closed the old one.
 *   3. Publish the banner's on-screen height to <html> as --dc-banner-h, so the product page's
 *      sticky add-to-cart bar can sit on top of it instead of overlapping it.
 *
 * ES5 IIFE, loaded with `defer` from the section. No dependencies.
 */
(function () {
  'use strict';

  var SEL = '[data-site-banner]';
  var VAR = '--dc-banner-h';
  var DAY = 24 * 60 * 60 * 1000;

  /**
   * Every localStorage access goes through here. Touching localStorage throws outright in Safari
   * private mode rather than failing soft, and the banner still has to work there — it just forgets
   * the dismissal. One guard for all three accessors instead of three separate try/catch blocks.
   */
  function safe(fn) {
    try {
      return fn();
    } catch (e) {
      return null;
    }
  }
  function read(key) {
    return safe(function () {
      return window.localStorage.getItem(key);
    });
  }
  function write(key, value) {
    safe(function () {
      window.localStorage.setItem(key, value);
    });
  }
  function drop(key) {
    safe(function () {
      window.localStorage.removeItem(key);
    });
  }

  /**
   * Has this banner been dismissed, and is that dismissal still current?
   *
   * The expiry is evaluated against the CURRENT value of "Remember close for" rather than one baked
   * in at dismissal time, so shortening the window in the theme editor immediately brings the
   * banner back for everyone instead of only affecting dismissals made from then on.
   */
  function dismissed(el, key) {
    var stamp = read(key);
    if (!stamp) {
      return false;
    }
    var days = parseInt(el.getAttribute('data-remember-days'), 10) || 0;
    if (days <= 0) {
      return true; /* forever */
    }
    var then = parseInt(stamp, 10);
    if (!then) {
      return true; /* present but unparseable — treat as dismissed rather than nagging */
    }
    if (Date.now() - then < days * DAY) {
      return true;
    }
    /* Expired — clear the key so it does not linger for another cycle. */
    drop(key);
    return false;
  }

  /**
   * The height the banner actually occupies at the bottom of the viewport.
   *
   * Measured from the TOPMOST edge among the bar and its close button, not from the bar alone: with
   * the close button on the corner it protrudes above the bar, and reporting only the bar's height
   * would let the add-to-cart bar stack straight over that button.
   *
   * The offsetHeight guard is load-bearing: "Show on desktop only" resolves to display:none on a
   * phone, a hidden element reports a zeroed rect, and innerHeight minus zero would otherwise
   * publish a full-viewport offset and push the add-to-cart bar off the top of the screen.
   */
  function height(el) {
    if (el.offsetHeight === 0) {
      return 0;
    }
    var edge = el.getBoundingClientRect().top;
    var button = el.querySelector('[data-site-banner-close]');
    var above;
    /* Toutes les positions « au-dessus de la barre », pas seulement la première. Le test portait sur
       l'égalité stricte avec 'outside' ; l'ajout de 'outside-start' (même hauteur, bord opposé) serait
       passé au travers, et la hauteur publiée aurait ignoré un bouton pourtant hors de la barre — la
       barre d'ajout au panier serait alors venue se poser dessus. Comparaison sur le préfixe, qui couvre
       les deux variantes et celles qu'on ajouterait ensuite ; 'inside' ne commence pas par 'outside'. */
    var mode = String(el.getAttribute('data-close'));
    if (button && mode.indexOf('outside') === 0) {
      above = button.getBoundingClientRect().top;
      if (above < edge) {
        edge = above;
      }
    }
    var h = window.innerHeight - edge;
    return h > 0 ? h : 0;
  }

  function publish(el, on) {
    var root = document.documentElement;
    /* Only the stacking mode reserves space. In "yield" mode the add-to-cart bar keeps the edge and
       this banner gets out of its way, so there is nothing to offset. */
    if (!on || el.getAttribute('data-atc') !== 'stack') {
      root.style.removeProperty(VAR);
      return;
    }
    root.style.setProperty(VAR, height(el) + 'px');
  }

  function init(scope) {
    var el = (scope || document).querySelector(SEL);
    if (!el || el.getAttribute('data-banner-bound') === '1') {
      return;
    }
    el.setAttribute('data-banner-bound', '1');

    var key = el.getAttribute('data-key') || 'dc-banner';
    /* In the theme editor the banner must always be visible, and closing it must not be remembered,
       or the merchant would dismiss it once and lose the ability to select and edit it. */
    var editing = window.Shopify ? !!window.Shopify.designMode : false;

    function reveal() {
      el.classList.add('is-ready');
      publish(el, true);
    }

    function close() {
      el.classList.remove('is-ready');
      publish(el, false);
      if (!editing) {
        write(key, String(Date.now()));
      }
    }

    function refresh() {
      if (el.classList.contains('is-ready')) {
        publish(el, true);
      }
    }

    var button = el.querySelector('[data-site-banner-close]');
    if (button) {
      button.addEventListener('click', close);
    }

    if (!editing && dismissed(el, key)) {
      return; /* stays hidden, and never reserves space */
    }

    var delay = parseFloat(el.getAttribute('data-delay')) || 0;
    if (delay > 0 && !editing) {
      window.setTimeout(reveal, delay * 1000);
    } else {
      /* Two frames: one for the element to be laid out, one so the transform transition has a
         previous value to animate from. Revealing in the same frame as layout skips the slide. */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(reveal);
      });
    }

    /* The bar's height changes with the viewport — the text rewraps and the mobile layout stacks —
       and the add-to-cart bar's offset has to follow. ResizeObserver catches a rewrap that a resize
       listener alone would miss, e.g. when the webfont lands and reflows the sentence. */
    if (window.ResizeObserver) {
      new window.ResizeObserver(refresh).observe(el);
    } else {
      window.addEventListener('resize', refresh);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme editor: the section is re-rendered on every settings change, which replaces the element
     and drops the binding with it. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    document.documentElement.style.removeProperty(VAR);
  });
})();
